<template>
  <div class="login-view" :style="rootStyles">
    <div v-if="showLoadingScreen" class="login-loading">
      <div class="login-loader" role="status" aria-live="polite" aria-label="Cargando tema"></div>
    </div>

    <div v-else class="login-container">
      <aside class="login-hero">
        <div class="login-hero__overlay">
          <div class="login-hero__card">
            <div v-if="currentLogoUrl" class="login-hero__logo-frame">
              <img
                :src="currentLogoUrl"
                :alt="brandingAltText"
                class="login-hero__logo"
              >
            </div>
            <h2 class="login-hero__name">{{ companyDisplayName }}</h2>
            <h3 class="login-hero__headline">{{ loginHeroHeadline }}</h3>
            <p class="login-hero__description">{{ loginHeroDescription }}</p>
          </div>
        </div>
      </aside>

      <section class="login-content">
        <div class="login-card">
          <div class="login-card__header">
            <img
              v-if="currentLogoUrl"
              :src="currentLogoUrl"
              :alt="brandingAltText"
              class="login-card__logo"
            >
            <h1 class="login-card__title">{{ loginCardTitle }}</h1>
            <p class="login-card__subtitle">{{ loginCardSubtitle }}</p>
          </div>

          <form @submit.prevent="sendLogin" class="login-form">
            <div class="field-group">
              <label class="field-label" for="usuario">Usuario o correo electrónico</label>
              <div class="input-field">
                <span class="input-field__icon" aria-hidden="true">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M15.75 5.25a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.5 20.25a8.25 8.25 0 1 1 15 0v.75H4.5v-.75Z"
                    />
                  </svg>
                </span>
                <input
                  id="usuario"
                  v-model="usuario"
                  type="email"
                  autocomplete="username"
                  placeholder="ejemplo@correo.com"
                  class="input-field__control"
                  required
                >
              </div>
            </div>

            <div class="field-group">
              <label class="field-label" for="password">Contraseña</label>
              <div class="input-field">
                <span class="input-field__icon" aria-hidden="true">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M16.5 10.5V7.5a4.5 4.5 0 1 0-9 0v3h-.75A1.5 1.5 0 0 0 5.25 12v7.5a1.5 1.5 0 0 0 1.5 1.5h10.5a1.5 1.5 0 0 0 1.5-1.5V12a1.5 1.5 0 0 0-1.5-1.5H16.5Z"
                    />
                  </svg>
                </span>
                <input
                  id="password"
                  v-model="password"
                  type="password"
                  autocomplete="current-password"
                  placeholder="••••••••"
                  class="input-field__control"
                  required
                >
              </div>
            </div>

            <div v-if="!userTypeLocked" class="field-group">
              <label class="field-label" for="userType">Tipo de usuario</label>
              <div class="input-field input-field--select">
                <span class="input-field__icon" aria-hidden="true">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M15 19.128a9.388 9.388 0 0 0 2.625.372 9.337 9.337 0 0 0 4.125-.952 4.125 4.125 0 0 0-6.75-3.173"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M8.25 21a6.375 6.375 0 0 0 6.363-6 4.125 4.125 0 1 0-7.488 2.354A6.35 6.35 0 0 0 8.25 21Z"
                    />
                  </svg>
                </span>
                <select id="userType" v-model="userType" class="input-field__control" required>
                  <option disabled value="">Seleccione tipo de usuario</option>
                  <option value="jobbing_admin">Administrador Jobbing</option>
                  <option value="property_manager">Administrador de Propiedades</option>
                  <option value="lead_partner">Lead Partner</option>
                </select>
              </div>
            </div>

            <div class="login-actions">
              <button type="button" class="login-link" @click="handleForgotPasswordClick">
                Olvidé mi contraseña
              </button>
              <button type="submit" class="login-submit" :disabled="loading">
                <svg
                  v-if="loading"
                  class="login-spinner"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="login-spinner__track"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path
                    class="login-spinner__segment"
                    fill="currentColor"
                    d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z"
                  />
                </svg>
                <span v-if="!loading">Ingresar al Sistema</span>
              </button>
            </div>
          </form>
        </div>
      </section>
      <transition name="login-modal-fade">
        <div
          v-if="forgotPasswordModalVisible"
          class="login-modal"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="forgotPasswordStep === 'reset' ? 'reset-password-title' : 'request-password-title'"
        >
          <div class="login-modal__backdrop" @click="handleForgotPasswordClose"></div>
          <div class="login-modal__dialog" role="document">
            <button
              type="button"
              class="login-modal__close"
              aria-label="Cerrar"
              @click="handleForgotPasswordClose"
            >
              &times;
            </button>
            <div class="login-modal__content">
              <template v-if="forgotPasswordStep === 'request'">
                <h2 id="request-password-title" class="login-modal__title">Recuperar contraseña</h2>
                <p class="login-modal__description">
                  Ingresa tu correo electrónico y te enviaremos un enlace para que puedas crear una nueva contraseña.
                </p>
                <form class="login-modal__form" @submit.prevent="submitForgotPasswordRequest">
                  <div class="field-group">
                    <label class="field-label" for="forgotEmail">Correo electrónico</label>
                    <div class="input-field">
                      <input
                        id="forgotEmail"
                        v-model="forgotEmail"
                        type="email"
                        autocomplete="email"
                        placeholder="ejemplo@correo.com"
                        class="input-field__control"
                        required
                      >
                    </div>
                  </div>
                  <div v-if="!userTypeLocked" class="field-group">
                    <label class="field-label" for="forgotUserType">Tipo de usuario</label>
                    <div class="input-field input-field--select">
                      <select id="forgotUserType" v-model="forgotUserType" class="input-field__control" required>
                        <option disabled value="">Seleccione tipo de usuario</option>
                        <option value="jobbing_admin">Administrador Jobbing</option>
                        <option value="property_manager">Administrador de Propiedades</option>
                        <option value="lead_partner">Lead Partner</option>
                      </select>
                    </div>
                  </div>
                  <p v-if="forgotPasswordError" class="login-modal__feedback login-modal__feedback--error">
                    {{ forgotPasswordError }}
                  </p>
                  <p v-if="forgotPasswordSuccess" class="login-modal__feedback">
                    {{ forgotPasswordSuccess }}
                  </p>
                  <div class="login-modal__actions">
                    <button
                      type="submit"
                      class="login-modal__primary"
                      :disabled="forgotPasswordLoading"
                    >
                      <span v-if="forgotPasswordLoading" class="login-modal__spinner"></span>
                      <span v-else>Enviar instrucciones</span>
                    </button>
                    <button type="button" class="login-modal__secondary" @click="handleForgotPasswordClose">
                      Cancelar
                    </button>
                  </div>
                </form>
              </template>
              <template v-else>
                <h2 id="reset-password-title" class="login-modal__title">Crear nueva contraseña</h2>
                <p class="login-modal__description">
                  Ingresa tu nueva contraseña. Debe tener entre 8 y 72 caracteres.
                </p>
                <form class="login-modal__form" @submit.prevent="submitPasswordReset">
                  <div class="field-group">
                    <label class="field-label" for="resetPassword">Nueva contraseña</label>
                    <div class="input-field">
                      <input
                        id="resetPassword"
                        v-model="resetNewPassword"
                        type="password"
                        autocomplete="new-password"
                        placeholder="Ingresa una nueva contraseña"
                        class="input-field__control"
                        required
                        minlength="8"
                        maxlength="72"
                      >
                    </div>
                  </div>
                  <div class="field-group">
                    <label class="field-label" for="resetPasswordConfirm">Confirmar contraseña</label>
                    <div class="input-field">
                      <input
                        id="resetPasswordConfirm"
                        v-model="resetConfirmPassword"
                        type="password"
                        autocomplete="new-password"
                        placeholder="Repite la nueva contraseña"
                        class="input-field__control"
                        required
                        minlength="8"
                        maxlength="72"
                      >
                    </div>
                  </div>
                  <p v-if="resetPasswordError" class="login-modal__feedback login-modal__feedback--error">
                    {{ resetPasswordError }}
                  </p>
                  <p v-if="resetPasswordSuccess" class="login-modal__feedback">
                    {{ resetPasswordSuccess }}
                  </p>
                  <div class="login-modal__actions">
                    <button
                      type="submit"
                      class="login-modal__primary"
                      :disabled="resetPasswordLoading"
                    >
                      <span v-if="resetPasswordLoading" class="login-modal__spinner"></span>
                      <span v-else>Actualizar contraseña</span>
                    </button>
                    <button type="button" class="login-modal__secondary" @click="handleForgotPasswordClose">
                      Cerrar
                    </button>
                  </div>
                </form>
              </template>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import defaultLogo from '@/assets/logo-jobbing-nuevo.webp';

export default {
  name: 'LoginView',
  data() {
    const defaultTheme = {
      background_gradient: '#f8fafc',
      card_background: '#ffffff',
      card_border_color: 'rgba(148, 163, 184, 0.2)',
      input_background: '#f8fafc',
      input_border_color: 'rgba(203, 213, 225, 1)',
      input_text_color: '#0f172a',
      text_color: '#0f172a',
      muted_text_color: '#475569',
      button_color: '#2563eb',
      button_hover_color: '#1d4ed8',
      secondary_color: '#7c3aed',
      focus_ring_color: 'rgba(37, 99, 235, 0.28)',
      footer_text: 'Jobbing.cl | Términos y condiciones',
      logo_url: defaultLogo,
    };

    return {
      usuario: '',
      password: '',
      loading: false,
      userType: '',
      defaultTheme,
      theme: { ...defaultTheme },
      branding: {
        logoUrl: defaultLogo,
        companyName: '',
      },
      empresaAtributos: {},
      userTypeLocked: false,
      defaultHosts: ['admin.jobbing.cl', 'localhost', '127.0.0.1'],
      awaitingThemedHost: false,
      themeReady: false,
      viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 0,
      zoomFactorValue: 1,
      zoomSyncHandle: null,
      forgotPasswordModalVisible: false,
      forgotPasswordStep: 'request',
      forgotEmail: '',
      forgotUserType: '',
      forgotPasswordLoading: false,
      forgotPasswordSuccess: '',
      forgotPasswordError: '',
      resetToken: '',
      resetNewPassword: '',
      resetConfirmPassword: '',
      resetPasswordLoading: false,
      resetPasswordSuccess: '',
      resetPasswordError: '',
    };
  },
  computed: {
    ...mapGetters(['admin_datos']),
    empresa() {
      return this.admin_datos?.empresa || null;
    },
    rootStyles() {
      return {
        ...this.cssVariables,
        '--login-min-height': this.adjustedMinHeight,
      };
    },
    cssVariables() {
      const normalizedPrimary = this.normalizeColor(this.theme.button_color);
      const primary = normalizedPrimary || this.defaultTheme.button_color;
      const primaryIsCustom = Boolean(normalizedPrimary) && !this.isDefaultValue('button_color');
      const rawSecondary = this.normalizeColor(this.theme.secondary_color);
      let secondary = rawSecondary;

      if (!secondary || this.isDefaultValue('secondary_color')) {
        secondary = primaryIsCustom ? primary : this.defaultTheme.secondary_color;
      }

      if (!secondary) {
        secondary = this.defaultTheme.secondary_color;
      }
      const hover = this.normalizeColor(this.theme.button_hover_color) || this.computeHoverColor(primary);
      const background = '#ffffff';
      const surface = this.theme.card_background || this.defaultTheme.card_background;
      const border = this.theme.card_border_color || this.defaultTheme.card_border_color;
      const inputBackground = this.theme.input_background || this.defaultTheme.input_background;
      const inputBorder = this.theme.input_border_color || this.defaultTheme.input_border_color;
      const inputText = this.theme.input_text_color || this.defaultTheme.input_text_color;
      const text = this.theme.text_color || this.defaultTheme.text_color;
      const muted = this.theme.muted_text_color || this.defaultTheme.muted_text_color;
      const focusRing = this.theme.focus_ring_color || this.computeFocusRing(primary);
      const buttonShadow = this.computeShadowColor(primary);

      return {
        '--background': background,
        '--primary': primary,
        '--primary-hover': hover,
        '--primary-foreground': this.getReadableTextColor(primary),
        '--secondary': secondary,
        '--secondary-foreground': this.getReadableTextColor(secondary),
        '--surface': surface,
        '--surface-border': border,
        '--surface-muted': inputBackground,
        '--surface-muted-border': inputBorder,
        '--surface-muted-text': inputText,
        '--text-color': text,
        '--muted-text': muted,
        '--ring': focusRing,
        '--primary-shadow': buttonShadow,
        '--hero-gradient': `linear-gradient(135deg, ${secondary}, ${primary})`,
        '--hero-card-gradient': 'linear-gradient(135deg, rgba(17, 24, 39, 0.94), rgba(8, 11, 18, 0.92))',
      };
    },
    adjustedMinHeight() {
      if (!this.viewportHeight || !Number.isFinite(this.viewportHeight)) {
        return '100dvh';
      }
      const zoom = this.zoomFactorValue && Number.isFinite(this.zoomFactorValue)
        ? this.zoomFactorValue
        : 1;
      const height = this.viewportHeight / zoom;
      return Number.isFinite(height) && height > 0 ? `${Math.ceil(height)}px` : '100dvh';
    },
    brandingAltText() {
      return this.branding.companyName
        ? `Logo de ${this.branding.companyName}`
        : 'Logo de Jobbing';
    },
    currentLogoUrl() {
      const atributos = this.admin_datos?.empresa?.atributos;
      if (atributos && atributos.logo_filename) {
        return atributos.logo_filename;
      }
      return this.branding.logoUrl;
    },
    showLoadingScreen() {
      return this.awaitingThemedHost && !this.themeReady;
    },
    loginPortalType() {
      if (this.userType === 'lead_partner') {
        return 'lead_partner';
      }
      const routeType = this.$route && this.$route.query ? this.$route.query.type : null;
      if (routeType === 'lead_partner') {
        return 'lead_partner';
      }
      return 'default';
    },
    companyDisplayName() {
      return (
        this.branding.companyName
        || this.empresa?.nombre
        || 'Jobbing'
      );
    },
    loginHeroHeadline() {
      if (this.loginPortalType === 'lead_partner') {
        return 'Seguimiento de Solicitudes y Ventas Generadas';
      }
      return 'Gestión Integral de Mantenimiento y Proyectos';
    },
    loginHeroDescription() {
      if (this.loginPortalType === 'lead_partner') {
        return 'Revisa el rendimiento de tu canal, consulta solicitudes atribuidas y monitorea ventas en un solo panel.';
      }
      return 'Nuestra plataforma centraliza la administración de órdenes, mantenciones y obras, con agenda, aprobaciones y reportes en tiempo real.';
    },
    loginCardTitle() {
      if (this.loginPortalType === 'lead_partner') {
        return `Bienvenido al portal de ${this.companyDisplayName}`;
      }
      return `¡Bienvenido a la administración de ${this.companyDisplayName}! 👋`;
    },
    loginCardSubtitle() {
      if (this.loginPortalType === 'lead_partner') {
        return 'Inicia sesión para revisar las solicitudes y ventas atribuidas a tu canal.';
      }
      return 'Por favor inicia sesión para gestionar órdenes, mantenciones y obras.';
    },
  },
  watch: {
    empresa: {
      handler(value) {
        if (value) {
          this.applyEmpresaBranding(value, true);
        }
      },
      immediate: true,
      deep: true,
    },
    '$route.query.token': {
      handler() {
        this.initializePasswordFlowFromRoute();
      },
      immediate: true,
    },
    '$route.query.type': {
      handler(value) {
        this.syncUserTypeFromRoute(value);
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions(['loginpro', 'loginPropertyManager', 'loginLeadPartner']),
    handleForgotPasswordClick() {
      this.resetForgotPasswordState();
      if (!this.forgotEmail) {
        this.forgotEmail = this.usuario || '';
      }
      if (this.userTypeLocked) {
        this.forgotUserType = this.userType || 'jobbing_admin';
      } else if (!this.forgotUserType && this.userType) {
        this.forgotUserType = this.userType;
      }
      if (this.resetToken) {
        this.forgotPasswordStep = 'reset';
      } else {
        this.forgotPasswordStep = 'request';
      }
      this.forgotPasswordModalVisible = true;
    },
    handleForgotPasswordClose() {
      this.forgotPasswordModalVisible = false;
      this.forgotPasswordStep = 'request';
      this.resetForgotPasswordState();
      this.resetNewPassword = '';
      this.resetConfirmPassword = '';
      if (this.resetToken) {
        this.clearResetRouteParams();
        this.resetToken = '';
      }
    },
    resetForgotPasswordState() {
      this.forgotPasswordError = '';
      this.forgotPasswordSuccess = '';
      this.resetPasswordError = '';
      this.resetPasswordSuccess = '';
    },
    async submitForgotPasswordRequest() {
      this.forgotPasswordError = '';
      this.forgotPasswordSuccess = '';

      const email = (this.forgotEmail || '').trim();
      if (!email) {
        this.forgotPasswordError = 'Debes ingresar tu correo electrónico.';
        return;
      }

      const userType = this.resolveForgotPasswordUserType();
      if (!userType) {
        this.forgotPasswordError = 'Selecciona el tipo de usuario para continuar.';
        return;
      }

      this.forgotPasswordLoading = true;
      const endpoint = userType === 'property_manager'
        ? `${window.hostname}api/property_manager/solicitar_recuperar_password`
        : userType === 'lead_partner'
          ? `${window.hostname}api/lead_partner/solicitar_recuperar_password`
          : `${window.hostname}api/admin/solicitar_recuperar_password`;

      const payload = { email };
      if (typeof window !== 'undefined' && window.location && window.location.origin) {
        payload.host = window.location.origin;
      }

      try {
        const response = await axios.post(endpoint, payload);
        const message = response && response.data && response.data.message;
        this.forgotPasswordSuccess = message
          || 'Si tu correo está registrado, recibirás un enlace para recuperar tu contraseña.';
      } catch (error) {
        this.forgotPasswordError = error?.response?.data?.message
          || 'No pudimos enviar el correo de recuperación. Inténtalo nuevamente en unos minutos.';
      } finally {
        this.forgotPasswordLoading = false;
      }
    },
    async submitPasswordReset() {
      this.resetPasswordError = '';
      this.resetPasswordSuccess = '';

      if (!this.resetToken) {
        this.resetPasswordError = 'El enlace de recuperación no es válido o expiró. Solicita uno nuevo.';
        return;
      }

      const password = (this.resetNewPassword || '').trim();
      const confirm = (this.resetConfirmPassword || '').trim();

      if (!password || !confirm) {
        this.resetPasswordError = 'Debes ingresar y confirmar la nueva contraseña.';
        return;
      }

      if (password.length < 8 || password.length > 72) {
        this.resetPasswordError = 'La contraseña debe tener entre 8 y 72 caracteres.';
        return;
      }

      if (password !== confirm) {
        this.resetPasswordError = 'Las contraseñas no coinciden.';
        return;
      }

      const userType = this.resolveForgotPasswordUserType();
      if (!userType) {
        this.resetPasswordError = 'No se pudo determinar el tipo de usuario.';
        return;
      }

      this.resetPasswordLoading = true;
      const endpoint = userType === 'property_manager'
        ? `${window.hostname}api/property_manager/reset_password`
        : userType === 'lead_partner'
          ? `${window.hostname}api/lead_partner/reset_password`
          : `${window.hostname}api/admin/reset_password`;

      try {
        const response = await axios.post(endpoint, {
          token: this.resetToken,
          new_password: password,
        });
        const message = response && response.data && response.data.message;
        this.resetPasswordSuccess = message || 'Tu contraseña fue actualizada correctamente.';
        this.resetNewPassword = '';
        this.resetConfirmPassword = '';
        this.clearResetRouteParams();
        this.resetToken = '';
      } catch (error) {
        this.resetPasswordError = error?.response?.data?.message
          || 'No pudimos actualizar tu contraseña. Revisa el enlace o solicita uno nuevo.';
      } finally {
        this.resetPasswordLoading = false;
      }
    },
    initializePasswordFlowFromRoute() {
      const token = this.$route && this.$route.query ? this.$route.query.token : null;
      if (token) {
        this.resetToken = String(token);
        this.forgotPasswordStep = 'reset';
        this.forgotPasswordModalVisible = true;
      } else {
        this.resetToken = '';
        if (!this.forgotPasswordModalVisible) {
          this.forgotPasswordStep = 'request';
        }
      }
    },
    syncUserTypeFromRoute(rawType) {
      if (!rawType || typeof rawType !== 'string') {
        return;
      }
      const normalized = rawType === 'property_manager' ? 'property_manager'
        : rawType === 'lead_partner' ? 'lead_partner'
          : rawType === 'jobbing_admin' ? 'jobbing_admin'
          : null;
      if (!normalized) {
        return;
      }
      if (this.userTypeLocked) {
        this.userType = normalized;
      } else if (!this.userType) {
        this.userType = normalized;
      }
      this.forgotUserType = normalized;
    },
    resolveForgotPasswordUserType() {
      if (this.userTypeLocked && this.userType) {
        return this.userType;
      }
      if (this.forgotUserType) {
        return this.forgotUserType;
      }
      if (this.userType) {
        return this.userType;
      }
      const routeType = this.$route && this.$route.query ? this.$route.query.type : null;
      if (routeType === 'property_manager' || routeType === 'lead_partner' || routeType === 'jobbing_admin') {
        return routeType;
      }
      return '';
    },
    clearResetRouteParams() {
      if (!this.$route || (!this.$route.query?.token && !this.$route.query?.type)) {
        return;
      }
      const nextQuery = { ...this.$route.query };
      delete nextQuery.token;
      delete nextQuery.type;
      this.$router.replace({ path: this.$route.path, query: nextQuery }).catch(() => {});
    },
    async initializeBranding() {
      const host = window.location.hostname;
      if (!host || this.defaultHosts.includes(host)) {
        this.awaitingThemedHost = false;
        this.themeReady = true;
        this.updateDocumentTitleAndFavicon();
        return;
      }

      this.awaitingThemedHost = true;
      this.setDocumentLoadingState();
      try {
        const response = await axios.get(`${window.hostname}api/property_manager/login_theme`, {
          params: { host },
        });
        const data = response.data || {};

        if (data.matched) {
          const forcedType = data.force_user_type || 'property_manager';
          this.userType = forcedType;
          this.userTypeLocked = Boolean(data.lock_user_type);
          if (forcedType && (this.userTypeLocked || !this.forgotUserType)) {
            this.forgotUserType = forcedType;
          }

          const tenant = data.empresa || data.lead_partner || null;
          if (tenant && tenant.nombre) {
            this.branding.companyName = tenant.nombre;
          }

          this.applyTheme(data.theme || {});
          this.applyEmpresaBranding(tenant || {}, false);
        }
      } catch (error) {
        console.error('No se pudo cargar el tema de login', error);
      } finally {
        this.themeReady = true;
        this.updateDocumentTitleAndFavicon();
      }
    },
    applyTheme(rawTheme) {
      if (!rawTheme || typeof rawTheme !== 'object') {
        return;
      }
      const normalized = this.normalizeThemeKeys(rawTheme);
      if (normalized.logo_url) {
        this.branding.logoUrl = normalized.logo_url;
      }
      this.theme = {
        ...this.theme,
        ...normalized,
      };

      if (
        Object.prototype.hasOwnProperty.call(normalized, 'button_color')
        && !Object.prototype.hasOwnProperty.call(normalized, 'button_hover_color')
      ) {
        this.theme.button_hover_color = this.computeHoverColor(this.theme.button_color);
      }

      if (
        Object.prototype.hasOwnProperty.call(normalized, 'button_color')
        && !Object.prototype.hasOwnProperty.call(normalized, 'focus_ring_color')
      ) {
        this.theme.focus_ring_color = this.computeFocusRing(this.theme.button_color);
      }
    },
    applyEmpresaBranding(empresaPayload, force = false) {
      if (!empresaPayload || typeof empresaPayload !== 'object') {
        return;
      }

      const atributosRaw = empresaPayload.atributos;
      let atributos = atributosRaw;
      if (atributos && typeof atributos === 'string') {
        try {
          atributos = JSON.parse(atributos);
        } catch (error) {
          atributos = {};
        }
      }
      atributos = atributos || {};

      this.empresaAtributos = atributos;

      if (empresaPayload.nombre) {
        this.branding.companyName = empresaPayload.nombre;
      }

      const logoFilename = atributos.logo_filename || atributos.logoUrl || atributos.logo_url;
      if (logoFilename) {
        this.branding.logoUrl = String(logoFilename);
      }

      const setThemeValue = (key, value, transform) => {
        if (!value) {
          return;
        }
        const normalizedValue = transform ? transform(value) : this.normalizeColor(value);
        if (!normalizedValue) {
          return;
        }
        if (force || this.isDefaultValue(key)) {
          this.theme[key] = normalizedValue;
        }
      };

      setThemeValue('background_gradient',
        atributos.background_gradient
        || atributos.background_color
        || atributos.background
        || atributos.color_fondo
      );

      setThemeValue('button_color',
        atributos.button_color
        || atributos.primary_color
        || atributos.color
      );

      setThemeValue('button_hover_color', atributos.button_hover_color, (color) => this.normalizeColor(color) || this.computeHoverColor(color));
      setThemeValue('focus_ring_color', atributos.focus_ring_color, (color) => this.normalizeColor(color) || this.computeFocusRing(color));

      setThemeValue('secondary_color',
        atributos.secondary_color
        || atributos.secondaryColor
        || atributos.accent_color
        || atributos.color_secundario
      );

      setThemeValue('text_color',
        atributos.text_color
        || atributos.textColor
        || atributos.titulo_color
      );

      setThemeValue('muted_text_color',
        atributos.muted_text_color
        || atributos.mutedTextColor
        || atributos.secondary_text_color
        || atributos.color_texto_secundario
      );

      setThemeValue('input_background', atributos.input_background || atributos.inputBackground);
      setThemeValue('input_border_color', atributos.input_border_color || atributos.inputBorderColor);
      setThemeValue('input_text_color', atributos.input_text_color || atributos.inputTextColor);
      setThemeValue('card_background', atributos.card_background || atributos.cardBackground);
      setThemeValue('card_border_color', atributos.card_border_color || atributos.cardBorderColor);

      if ((force || this.isDefaultValue('button_hover_color')) && !atributos.button_hover_color && this.theme.button_color) {
        this.theme.button_hover_color = this.computeHoverColor(this.theme.button_color);
      }

      if ((force || this.isDefaultValue('focus_ring_color')) && !atributos.focus_ring_color && this.theme.button_color) {
        this.theme.focus_ring_color = this.computeFocusRing(this.theme.button_color);
      }

      this.updateDocumentTitleAndFavicon(atributos);
    },
    normalizeColor(value) {
      if (typeof value !== 'string') {
        return null;
      }
      const trimmed = value.trim();
      return trimmed || null;
    },
    isDefaultValue(key) {
      return this.theme[key] === this.defaultTheme[key];
    },
    computeHoverColor(color) {
      if (typeof color !== 'string') {
        return color;
      }

      const trimmed = color.trim();
      if (!trimmed.startsWith('#')) {
        return trimmed;
      }

      let hex = trimmed.slice(1);
      if (![3, 6].includes(hex.length)) {
        return trimmed;
      }

      if (hex.length === 3) {
        hex = hex.split('').map((char) => char + char).join('');
      }

      const channels = [hex.slice(0, 2), hex.slice(2, 4), hex.slice(4, 6)];
      const darker = channels.map((segment) => {
        const value = parseInt(segment, 16);
        if (Number.isNaN(value)) {
          return segment;
        }
        const adjusted = Math.max(0, Math.min(255, Math.floor(value * 0.85)));
        return adjusted.toString(16).padStart(2, '0');
      });

      return `#${darker.join('')}`;
    },
    computeFocusRing(color) {
      if (typeof color !== 'string') {
        return color;
      }

      const trimmed = color.trim();
      if (!trimmed.startsWith('#')) {
        return trimmed;
      }

      let hex = trimmed.slice(1);
      if (![3, 6].includes(hex.length)) {
        return trimmed;
      }

      if (hex.length === 3) {
        hex = hex.split('').map((char) => char + char).join('');
      }

      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);

      if ([r, g, b].some((channel) => Number.isNaN(channel))) {
        return trimmed;
      }

      return `rgba(${r}, ${g}, ${b}, 0.35)`;
    },
    computeShadowColor(color) {
      if (typeof color !== 'string') {
        return 'rgba(37, 99, 235, 0.18)';
      }

      const trimmed = color.trim();
      if (!trimmed.startsWith('#')) {
        return 'rgba(37, 99, 235, 0.18)';
      }

      let hex = trimmed.slice(1);
      if (![3, 6].includes(hex.length)) {
        return 'rgba(37, 99, 235, 0.18)';
      }

      if (hex.length === 3) {
        hex = hex.split('').map((char) => char + char).join('');
      }

      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);

      if ([r, g, b].some((channel) => Number.isNaN(channel))) {
        return 'rgba(37, 99, 235, 0.18)';
      }

      return `rgba(${r}, ${g}, ${b}, 0.22)`;
    },
    normalizeThemeKeys(theme) {
      const mapping = {
        backgroundGradient: 'background_gradient',
        background_gradient: 'background_gradient',
        backgroundColor: 'background_gradient',
        background_color: 'background_gradient',
        cardBackground: 'card_background',
        card_background: 'card_background',
        cardBorderColor: 'card_border_color',
        card_border_color: 'card_border_color',
        inputBackground: 'input_background',
        input_background: 'input_background',
        inputBorderColor: 'input_border_color',
        input_border_color: 'input_border_color',
        inputTextColor: 'input_text_color',
        input_text_color: 'input_text_color',
        textColor: 'text_color',
        text_color: 'text_color',
        mutedTextColor: 'muted_text_color',
        muted_text_color: 'muted_text_color',
        buttonColor: 'button_color',
        button_color: 'button_color',
        primaryColor: 'button_color',
        primary_color: 'button_color',
        buttonHoverColor: 'button_hover_color',
        button_hover_color: 'button_hover_color',
        secondaryColor: 'secondary_color',
        secondary_color: 'secondary_color',
        focusRingColor: 'focus_ring_color',
        focus_ring_color: 'focus_ring_color',
        footerText: 'footer_text',
        footer_text: 'footer_text',
        logoUrl: 'logo_url',
        logo_url: 'logo_url',
        logoFilename: 'logo_url',
        logo_filename: 'logo_url',
      };
      const normalized = {};
      Object.keys(mapping).forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(theme, key)) {
          const rawValue = theme[key];
          if (rawValue !== undefined && rawValue !== null) {
            const value = String(rawValue).trim();
            if (value !== '') {
              normalized[mapping[key]] = value;
            }
          }
        }
      });
      return normalized;
    },
    updateViewportHeight() {
      if (typeof window !== 'undefined') {
        this.viewportHeight = window.innerHeight;
      }
      this.updateZoomFactor();
      this.scheduleZoomSync();
    },
    updateZoomFactor() {
      if (typeof document === 'undefined' || !document.body) {
        this.zoomFactorValue = 1;
        return;
      }
      const zoomValue = parseFloat(document.body.style.zoom);
      this.zoomFactorValue = Number.isFinite(zoomValue) && zoomValue > 0 ? zoomValue : 1;
    },
    scheduleZoomSync() {
      if (typeof window === 'undefined') {
        return;
      }
      if (this.zoomSyncHandle) {
        window.clearTimeout(this.zoomSyncHandle);
      }
      this.zoomSyncHandle = window.setTimeout(() => {
        this.updateZoomFactor();
        this.zoomSyncHandle = null;
      }, 120);
    },
    deriveSecondaryColor(primary) {
      if (!primary || typeof primary !== 'string') {
        return this.defaultTheme.secondary_color;
      }
      const trimmed = primary.trim();
      if (!trimmed.startsWith('#')) {
        return trimmed;
      }
      let hex = trimmed.slice(1);
      if (hex.length === 3) {
        hex = hex.split('').map((char) => char + char).join('');
      }
      if (hex.length !== 6) {
        return trimmed;
      }
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      if ([r, g, b].some((channel) => Number.isNaN(channel))) {
        return trimmed;
      }
      const mix = (channel) => {
        const mixed = Math.floor((channel + 255) / 2);
        return mixed.toString(16).padStart(2, '0');
      };
      return `#${mix(r)}${mix(g)}${mix(b)}`;
    },
    getReadableTextColor(color) {
      if (typeof color !== 'string') {
        return '#ffffff';
      }
      const trimmed = color.trim();
      if (!trimmed.startsWith('#')) {
        return '#ffffff';
      }
      let hex = trimmed.slice(1);
      if (hex.length === 3) {
        hex = hex.split('').map((char) => char + char).join('');
      }
      if (hex.length !== 6) {
        return '#ffffff';
      }
      const r = parseInt(hex.slice(0, 2), 16) / 255;
      const g = parseInt(hex.slice(2, 4), 16) / 255;
      const b = parseInt(hex.slice(4, 6), 16) / 255;
      if ([r, g, b].some((channel) => Number.isNaN(channel))) {
        return '#ffffff';
      }
      const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      return luminance > 0.6 ? '#0f172a' : '#ffffff';
    },
    sendLogin() {
      if (this.loading) return;

      if (!this.usuario || !this.password || !this.userType) {
        this.$notify({
          group: 'foo',
          title: 'Error',
          type: 'warn',
          text: 'Por favor, complete todos los campos.',
        });
        return;
      }

      this.loading = true;
      const userData = { email: this.usuario, password: this.password };

      if (this.userType === 'jobbing_admin') {
        this.loginpro(userData)
          .then(() => {
            this.loading = false;
            this.$router.push('/Dashboard');
            this.$notify({
              group: 'foos',
              title: '¡Éxito!',
              type: 'success',
              text: '¡Has iniciado sesión!',
            });
          })
          .catch((e) => {
            this.loading = false;
            this.$notify({
              group: 'foo',
              title: '¡Error!',
              type: 'warn',
              text: e.response?.data || 'No se pudo iniciar sesión.',
            });
          });
      } else if (this.userType === 'property_manager') {
        this.loginPropertyManager(userData)
          .then(() => {
            this.loading = false;
            this.$router.push('/administrador-propiedades/Dashboard');
            this.$notify({
              group: 'foos',
              title: '¡Éxito!',
              type: 'success',
              text: '¡Has iniciado sesión!',
            });
          })
          .catch((e) => {
            this.loading = false;
            this.$notify({
              group: 'foo',
              title: '¡Error!',
              type: 'warn',
              text: e.response?.data || 'No se pudo iniciar sesión.',
            });
          });
      } else if (this.userType === 'lead_partner') {
        this.loginLeadPartner(userData)
          .then(() => {
            this.loading = false;
            this.$router.push('/lead-partner/Dashboard');
            this.$notify({
              group: 'foos',
              title: '¡Éxito!',
              type: 'success',
              text: '¡Has iniciado sesión!',
            });
          })
          .catch((e) => {
            this.loading = false;
            this.$notify({
              group: 'foo',
              title: '¡Error!',
              type: 'warn',
              text: e.response?.data || 'No se pudo iniciar sesión.',
            });
          });
      } else {
        this.loading = false;
        this.$notify({
          group: 'foo',
          title: 'Error',
          type: 'warn',
          text: 'Tipo de usuario inválido.',
        });
      }
    },
    isCustomHost() {
      if (typeof window === 'undefined') {
        return false;
      }
      const host = window.location.hostname;
      return Boolean(host) && !this.defaultHosts.includes(host);
    },
    setDocumentTitle(title) {
      if (typeof document === 'undefined' || typeof title !== 'string') {
        return;
      }
      document.title = title;
    },
    clearFaviconLinks() {
      if (typeof document === 'undefined') {
        return;
      }
      const links = Array.from(document.querySelectorAll('link[rel*="icon"]'));
      links.forEach((link) => {
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
      });
    },
    setFavicon(href) {
      if (typeof document === 'undefined') {
        return;
      }
      this.clearFaviconLinks();
      if (!href) {
        return;
      }
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = href;
      document.head.appendChild(link);
    },
    extractFaviconUrl(atributos = {}) {
      if (!atributos || typeof atributos !== 'object') {
        return null;
      }
      const faviconCandidate = atributos.favicon
        || atributos.favicon_url
        || atributos.faviconUrl
        || atributos.favicon_filename
        || atributos.faviconFilename;
      if (!faviconCandidate) {
        return null;
      }
      return String(faviconCandidate).trim() || null;
    },
    setDocumentLoadingState() {
      if (!this.isCustomHost()) {
        return;
      }
      this.setDocumentTitle('Cargando...');
      this.clearFaviconLinks();
    },
    updateDocumentTitleAndFavicon(atributos = null) {
      if (!this.isCustomHost()) {
        return;
      }
      const atributosToUse = atributos || this.empresaAtributos || {};
      this.setDocumentTitle(this.companyDisplayName);
      const faviconUrl = this.extractFaviconUrl(atributosToUse);
      if (faviconUrl) {
        this.setFavicon(faviconUrl);
      } else {
        this.clearFaviconLinks();
      }
    },
  },
  mounted() {
    this.initializePasswordFlowFromRoute();
    const routeType = this.$route && this.$route.query ? this.$route.query.type : null;
    if (routeType) {
      this.syncUserTypeFromRoute(routeType);
    }
    this.initializeBranding();
    this.updateViewportHeight();
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        this.updateViewportHeight();
        this.scheduleZoomSync();
      });
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.updateViewportHeight);
    }
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.updateViewportHeight);
      if (this.zoomSyncHandle) {
        window.clearTimeout(this.zoomSyncHandle);
        this.zoomSyncHandle = null;
      }
    }
  },
};
</script>

<style scoped>
.login-view {
  min-height: var(--login-min-height, 100dvh);
  height: var(--login-min-height, 100dvh);
  width: 100%;
  display: flex;
  flex-direction: column;
  background: var(--background, #f8fafc);
  color: var(--text-color, #0f172a);
  font-family: 'Inter', sans-serif;
}

.login-loading {
  min-height: var(--login-min-height, 100dvh);
  height: var(--login-min-height, 100dvh);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  color: var(--text-color, #0f172a);
}

.login-loader {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: 6px solid rgba(148, 163, 184, 0.25);
  border-top-color: var(--primary, #2563eb);
  animation: login-spin 0.9s linear infinite;
}

@keyframes login-spin {
  to {
    transform: rotate(360deg);
  }
}

.login-container {
  flex: 1 1 auto;
  display: flex;
  min-height: var(--login-min-height, 100dvh);
  height: var(--login-min-height, 100dvh);
}

.login-hero {
  display: none;
  flex: 1 1 50%;
  background-image: var(--hero-gradient, linear-gradient(135deg, #7c3aed, #2563eb));
  color: var(--secondary-foreground, #ffffff);
  min-height: var(--login-min-height, 100dvh);
  height: var(--login-min-height, 100dvh);
}

.login-hero__overlay {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 40px;
}

.login-hero__card {
  width: min(560px, 100%);
  background-image: var(--hero-card-gradient, linear-gradient(135deg, rgba(17, 24, 39, 0.94), rgba(8, 11, 18, 0.92)));
  border-radius: 28px;
  padding: 48px 40px;
  display: flex;
  flex-direction: column;
  gap: 28px;
  box-shadow: 0 32px 80px rgba(15, 23, 42, 0.45);
  color: #ffffff;
}

.login-hero__logo-frame {
  display: grid;
  place-items: center;
  width: min(82%, 320px);
  height: 112px;
  align-self: center;
}

.login-hero__logo {
  display: block;
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  filter:
    drop-shadow(1px 0 0 rgba(255, 255, 255, 0.92))
    drop-shadow(-1px 0 0 rgba(255, 255, 255, 0.92))
    drop-shadow(0 1px 0 rgba(255, 255, 255, 0.92))
    drop-shadow(0 -1px 0 rgba(255, 255, 255, 0.92))
    drop-shadow(0 10px 20px rgba(0, 0, 0, 0.3));
}

.login-hero__name {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
}

.login-hero__headline {
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0;
  color: rgba(255, 255, 255, 0.92);
}

.login-hero__description {
  margin: 0;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.6;
  font-size: 0.98rem;
}

.login-content {
  flex: 1 1 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  min-height: var(--login-min-height, 100dvh);
  height: var(--login-min-height, 100dvh);
  padding: 48px 24px;
}

.login-card {
  width: min(610px, 100%);
  background-color: var(--surface, #ffffff);
  border-radius: 24px;
  border: 1px solid var(--surface-border, rgba(148, 163, 184, 0.2));
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.12);
  padding: 48px 40px;
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.login-card__header {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.login-card__logo {
  max-height: 110px;
  max-width: min(75vw, 240px);
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
  margin: 0 auto;
}

.login-card__title {
  font-size: 1.85rem;
  font-weight: 700;
  margin: 0;
  color: var(--primary, #2563eb);
  line-height: 1.3;
}

.login-card__subtitle {
  margin: 0;
  color: var(--muted-text, #475569);
  line-height: 1.6;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--muted-text, #475569);
  text-align: left;
}

.input-field {
  position: relative;
  display: flex;
  align-items: center;
}

.input-field__icon {
  position: absolute;
  left: 18px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  color: var(--muted-text, #475569);
  pointer-events: none;
  display: inline-flex;
}

.input-field__control {
  width: 100%;
  padding: 14px 18px 14px 52px;
  font-size: 1rem;
  border-radius: 14px;
  border: 1px solid var(--surface-muted-border, rgba(203, 213, 225, 1));
  background-color: var(--surface-muted, #f8fafc);
  color: var(--surface-muted-text, #0f172a);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
}

.input-field__control:focus {
  outline: none;
  border-color: var(--primary, #2563eb);
  box-shadow: 0 0 0 4px var(--ring, rgba(37, 99, 235, 0.28));
  background-color: #ffffff;
}

.input-field--select .input-field__control {
  padding-right: 48px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23475669' viewBox='0 0 24 24'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 18px center;
  background-size: 18px;
}

.input-field--select select::-ms-expand {
  display: none;
}

.login-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 12px;
}

.login-link {
  background: none;
  border: none;
  padding: 0;
  color: var(--muted-text, #475569);
  font-size: 0.875rem;
  text-decoration: none;
  transition: color 0.2s ease;
  cursor: pointer;
}

.login-link:focus {
  outline: none;
}

.login-link:focus-visible,
.login-link:hover {
  color: var(--primary, #2563eb);
}

.login-submit {
  background-color: var(--primary, #2563eb);
  color: var(--primary-foreground, #ffffff);
  padding: 12px 28px;
  border-radius: 14px;
  border: none;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 18px 32px var(--primary-shadow, rgba(37, 99, 235, 0.18));
  min-height: 48px;
}

.login-submit:hover:not(:disabled) {
  background-color: var(--primary-hover, #1d4ed8);
  transform: translateY(-1px);
}

.login-submit:focus-visible {
  outline: none;
  box-shadow: 0 0 0 4px var(--ring, rgba(37, 99, 235, 0.28));
}

.login-submit:disabled {
  cursor: not-allowed;
  opacity: 0.7;
  transform: none;
  box-shadow: none;
}

.login-spinner {
  width: 20px;
  height: 20px;
  animation: login-spin 0.7s linear infinite;
}

.login-spinner__track {
  opacity: 0.25;
}

.login-spinner__segment {
  opacity: 0.85;
}

.login-modal-fade-enter-active,
.login-modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.login-modal-fade-enter-active .login-modal__dialog,
.login-modal-fade-leave-active .login-modal__dialog {
  transition: transform 0.2s ease;
}

.login-modal-fade-enter,
.login-modal-fade-leave-to {
  opacity: 0;
}

.login-modal-fade-enter .login-modal__dialog,
.login-modal-fade-leave-to .login-modal__dialog {
  transform: translateY(-8px);
}

.login-modal {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.login-modal__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.58);
  backdrop-filter: blur(2px);
}

.login-modal__dialog {
  position: relative;
  width: min(440px, calc(100% - 32px));
  background: #ffffff;
  border-radius: 18px;
  padding: 32px 28px;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.18);
  z-index: 1;
}

.login-modal__close {
  position: absolute;
  top: 12px;
  right: 14px;
  background: none;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  color: var(--muted-text, #475569);
  cursor: pointer;
  padding: 4px;
}

.login-modal__close:focus {
  outline: none;
}

.login-modal__close:focus-visible,
.login-modal__close:hover {
  color: var(--primary, #2563eb);
}

.login-modal__content {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.login-modal__title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--text-color, #0f172a);
}

.login-modal__description {
  margin: 0;
  color: var(--muted-text, #475569);
  font-size: 0.95rem;
}

.login-modal__form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.login-modal__feedback {
  margin: 0;
  font-size: 0.9rem;
  color: var(--secondary, #7c3aed);
}

.login-modal__feedback--error {
  color: #dc2626;
}

.login-modal__actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.login-modal__primary,
.login-modal__secondary {
  border-radius: 12px;
  padding: 10px 20px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
}

.login-modal__primary {
  background-color: var(--primary, #2563eb);
  color: var(--primary-foreground, #ffffff);
  box-shadow: 0 14px 28px var(--primary-shadow, rgba(37, 99, 235, 0.18));
}

.login-modal__primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.login-modal__primary:hover:not(:disabled) {
  background-color: var(--primary-hover, #1d4ed8);
  transform: translateY(-1px);
  box-shadow: 0 20px 32px var(--primary-shadow, rgba(37, 99, 235, 0.22));
}

.login-modal__secondary {
  background-color: rgba(148, 163, 184, 0.14);
  color: var(--muted-text, #475569);
}

.login-modal__secondary:hover {
  background-color: rgba(148, 163, 184, 0.28);
}

.login-modal__spinner {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.6);
  border-top-color: rgba(255, 255, 255, 1);
  animation: login-spin 0.8s linear infinite;
  display: inline-block;
}

@media (min-width: 1024px) {
  .login-hero {
    display: flex;
  }

  .login-card__logo {
    display: none;
  }

  .login-card__header {
    text-align: left;
    align-items: flex-start;
  }
}

@media (max-width: 1023px) {
  .login-content {
    padding: 40px 20px;
  }

  .login-card {
    border-radius: 20px;
    padding: 40px 32px;
  }
}

@media (max-width: 640px) {
  .login-card {
    padding: 32px 24px;
    gap: 28px;
  }

  .login-card__logo {
    max-height: 90px;
    max-width: min(65vw, 200px);
    width: auto;
    height: auto;
  }

  .login-card__title {
    font-size: 1.65rem;
  }

  .input-field__control {
    padding-left: 50px;
  }

}
</style>

